import React, { useContext, useEffect, useMemo, useState } from 'react';
import styled from 'styled-components';
import { Button, Switch } from '@material-ui/core';
import ModuleContext from 'CMFW/context/ModuleContext';
import Grid from 'CMFW/grid/Grid';
import { LoadingItem } from 'CMFW/layout/Loading';
import { IFields } from 'CMFW/grid/fields';
import { asyncTaskStatusRenderer } from 'CMFW/grid/cellRenderers/asyncTaskStatus';
import { translationItemInfoRenderer } from 'CMFW/grid/cellRenderers/translationItemInfo';
import { translationConfidenceRenderer } from 'CMFW/grid/cellRenderers/translationConfidence';
import { translationReasoningRenderer } from 'CMFW/grid/cellRenderers/translationReasoning';
import { validationStatusRenderer } from 'CMFW/grid/cellRenderers/validationStatus';
import TranslationProjectTranslationItemsModel from 'models/TranslationProjectTranslationItemsModel';
import TranslationBatchModel, { IBatchJob, IBatchProgress, BatchJobStatus, BatchJobMode } from 'models/TranslationBatchModel';
import UnifiedBatchSummary from './UnifiedBatchSummary';

interface IProps {
  job: IBatchJob;
  batchModel: TranslationBatchModel;
  itemsModel: TranslationProjectTranslationItemsModel;
  forceRender: () => void;
  dialectId: number;
}

const UnifiedBatchDetails: React.FC<IProps> = ({ job, batchModel, itemsModel, forceRender, dialectId }) => {
  const gridModel = useContext(ModuleContext);
  const isActive = job.status !== BatchJobStatus.DONE && job.status !== BatchJobStatus.FAILED && job.status !== BatchJobStatus.CANCELLED;
  const isDone = job.status === BatchJobStatus.DONE;
  const [deleteEnabled, setDeleteEnabled] = useState(false);
  const [progress, setProgress] = useState<IBatchProgress | null>(null);
  // Items grid is opt-in per job — large bulks can be slow to load and the
  // user often only needs the totals/summary. Reset whenever the job changes.
  const [itemsLoaded, setItemsLoaded] = useState(false);

  useEffect(() => {
    if (!isActive) {
      setProgress(null);
      return;
    }

    let cancelled = false;

    const fetchOnce = async () => {
      const p = await batchModel.getProgress(job.id);
      if (!cancelled) {
        setProgress(p);
      }
    };

    fetchOnce();
    const timer = window.setInterval(fetchOnce, 15000);

    return () => {
      cancelled = true;
      window.clearInterval(timer);
    };
  }, [job.id, isActive, batchModel]);

  // Reset delete switch and item-load state when job changes
  useEffect(() => {
    setDeleteEnabled(false);
    setItemsLoaded(false);
  }, [job.id]);

  const handleLoadItems = () => {
    if (job.projectId === null) return;
    itemsModel.setProjectId(job.projectId);
    setItemsLoaded(true);
  };

  const cellRenderers = useMemo(
    () => ({
      [IFields.moduleNavigation]: 'moduleNavigationRenderer',
      [IFields.async_task_status]: asyncTaskStatusRenderer,
      [IFields.agent_prompt_log]: 'agentPromptLogRenderer',
      [IFields.is_validated]: validationStatusRenderer,
      [IFields.item_info]: translationItemInfoRenderer,
      [IFields.confidence]: translationConfidenceRenderer,
      [IFields.reasoning]: translationReasoningRenderer,
    }),
    [],
  );

  const handleDelete = async () => {
    await gridModel.delete();
    setDeleteEnabled(false);
  };

  return (
    <Container>
      <Header>
        <Title>
          {job.name}
        </Title>
        <ModeBadge $mode={job.mode}>{job.modeLabel}</ModeBadge>
        <StatusBadge $status={job.status}>{job.statusLabel}</StatusBadge>
      </Header>

      <MetaRow>
        <span>Total: {job.totalItems}</span>
        <span>Created: {job.createdAt}</span>
      </MetaRow>

      <UnifiedBatchSummary job={job} progress={progress} />

      {isDone && job.projectId && !itemsLoaded && (
        <LoadItemsBar>
          <span>Items not loaded — totals only.</span>
          <Button variant="contained" size="small" color="primary" onClick={handleLoadItems}>
            Load {job.totalItems.toLocaleString()} items
          </Button>
        </LoadItemsBar>
      )}

      {isDone && job.projectId && itemsLoaded && (
        <GridContainer>
          <ModuleContext.Provider value={itemsModel}>
            <Grid cellRenderers={cellRenderers} />
          </ModuleContext.Provider>
          <LoadingItem />
        </GridContainer>
      )}

      <DeleteContainer>
        <Switch
          checked={deleteEnabled}
          onChange={() => setDeleteEnabled(!deleteEnabled)}
          color="secondary"
          size="small"
        />
        <Button
          variant="contained"
          size="small"
          color="secondary"
          onClick={handleDelete}
          disabled={!deleteEnabled}
        >
          delete
        </Button>
      </DeleteContainer>
    </Container>
  );
};

export default UnifiedBatchDetails;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 16px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.div`
  font-weight: 600;
  font-size: 14px;
  flex: 1;
`;

const ModeBadge = styled.span<{ $mode: number }>`
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => (p.$mode === BatchJobMode.MEMORY_ONLY ? '#e8f5e9' : '#e3f2fd')};
  color: ${(p) => (p.$mode === BatchJobMode.MEMORY_ONLY ? '#2e7d32' : '#1565c0')};
`;

const StatusBadge = styled.span<{ $status: number }>`
  padding: 2px 8px;
  border-radius: 3px;
  font-size: 11px;
  font-weight: 600;
  background: ${(p) => {
    if (p.$status === BatchJobStatus.DONE) return '#e8f5e9';
    if (p.$status === BatchJobStatus.FAILED) return '#ffebee';
    if (p.$status === BatchJobStatus.CANCELLED) return '#fff3e0';
    return '#e3f2fd';
  }};
  color: ${(p) => {
    if (p.$status === BatchJobStatus.DONE) return '#2e7d32';
    if (p.$status === BatchJobStatus.FAILED) return '#c62828';
    if (p.$status === BatchJobStatus.CANCELLED) return '#e65100';
    return '#1565c0';
  }};
`;

const MetaRow = styled.div`
  display: flex;
  gap: 16px;
  padding: 6px 16px;
  font-size: 12px;
  color: #666;
  border-bottom: 1px solid #f0f0f0;
`;

const GridContainer = styled.div`
  flex: 1;
  overflow: auto;
  position: relative;
`;

const LoadItemsBar = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  padding: 12px 16px;
  background: #fafafa;
  border-bottom: 1px solid #e0e0e0;
  font-size: 13px;
  color: #666;
`;

const DeleteContainer = styled.div`
  display: flex;
  align-items: center;
  padding: 4px 16px;
  border-top: 1px solid #e0e0e0;
`;
